/**
 * AdminMetricDetailDialog — the org-scoped drill-in for one practice metric.
 *
 * Reuses the practice-owner loop card (the annotated trend where intervention
 * markers visibly land) but fetches through the org-scoped admin component route —
 * the SAME server-ui endpoint cache entry the grid tile uses, so adding a marker
 * refreshes both at once. The owner-only findings console is intentionally dropped:
 * it scopes to the logged-in org, not the admin-chosen target.
 */
import React, { useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetServerUiComponentByEndpointQuery } from '@features/server-ui/store/api';
import {
  formatValue,
  loopFromPayload,
} from '@features/semantic-layer/components/detailAdapters';
import { MetricLoopCard } from '@features/semantic-layer/components/MetricLoopCard';
import type { SemanticMetricTilePayload } from '@features/semantic-layer/types';

const componentEndpoint = (organizationId: string, slug: string) =>
  `/v2/admin/semantic/organizations/${organizationId}/analytics/components/${slug}`;

interface AdminMetricDetailDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  slug: string | null;
  onAddIntervention: (slug: string, name?: string) => void;
}

export const AdminMetricDetailDialog: React.FC<AdminMetricDetailDialogProps> = ({
  open,
  onClose,
  organizationId,
  slug,
  onAddIntervention,
}) => {
  const { data, isLoading, isError } = useGetServerUiComponentByEndpointQuery(
    open && slug ? componentEndpoint(organizationId, slug) : skipToken,
  );

  const payload = data?.data as SemanticMetricTilePayload | undefined;
  const loopData = useMemo(
    () => (payload ? loopFromPayload(payload) : null),
    [payload],
  );
  const showLoop =
    payload?.measure === 'ratio' && !!loopData && loopData.series.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {payload?.display_name ?? slug ?? 'Metric'}
        {payload && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {[payload.applies_to_label, payload.measure, payload.grader_kind]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        )}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && !isLoading && (
          <Alert severity="error">Couldn&apos;t load this metric for the organization.</Alert>
        )}
        {!isLoading && !isError && payload && (
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {showLoop && loopData ? (
              <MetricLoopCard data={loopData} />
            ) : (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  p: 2.5,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {formatValue(payload)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {payload.n != null ? `n=${payload.n} · trailing 90 days` : ''}
                </Typography>
              </Box>
            )}

            {payload.lever?.label && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: t => alpha(t.palette.warning.main, 0.35),
                  bgcolor: t => alpha(t.palette.warning.main, 0.08),
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'warning.dark',
                  }}
                >
                  How we&apos;d help
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {payload.lever.label}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!slug}
          onClick={() => slug && onAddIntervention(slug, payload?.display_name ?? undefined)}
        >
          Add intervention
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminMetricDetailDialog;
